import { useState } from "react";
import {
  Calendar,
  Globe,
  Home,
  Map,
  Settings,
  Users,
  Link,
  Tags,
  PenTool,
  Layout,
  Import,
  Image,
  Share2,
  ChevronRight,
  Gift,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useWebsiteSelection } from "@/context/WebsiteSelectionContext";
import { useWebsites } from "@/context/WebsitesContext";
import { useAppState } from "@/context/AppStateContext";
import { Logo } from "@/components/Logo";

export function AppSidebar() {
  const { currentWebsite } = useWebsites();
  const { openModal } = useWebsiteSelection();
  const { currentRightPaneUrl, setCurrentRightPaneUrl } = useAppState();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSettingsExpanded, setIsSettingsExpanded] = useState(
    currentRightPaneUrl?.startsWith("/settings") || false
  );

  const settingsItems = [
    {
      id: "wordpress",
      label: "WordPress Integration",
      icon: <Link className="h-4 w-4" />,
    },
    {
      id: "publication",
      label: "Publication Settings",
      icon: <Calendar className="h-4 w-4" />,
    },
    { id: "keywords", label: "Keyword Management", icon: <Tags className="h-4 w-4" /> },
    { id: "concepts", label: "Themes", icon: <Tags className="h-4 w-4" /> },
    { id: "writing", label: "Writing Style", icon: <PenTool className="h-4 w-4" /> },
    {
      id: "formatting",
      label: "Content Formatting",
      icon: <Layout className="h-4 w-4" />,
    },
    { id: "language", label: "Website Language", icon: <Globe className="h-4 w-4" /> },
    { id: "import", label: "Import Limits", icon: <Import className="h-4 w-4" /> },
    { id: "images", label: "AI Image Generation", icon: <Image className="h-4 w-4" /> },
    { id: "social", label: "Social Media", icon: <Share2 className="h-4 w-4" /> },
  ];

  const handleSettingsClick = () => {
    setIsSettingsExpanded(!isSettingsExpanded);
    handleNavigation("/settings");
  };

  const handleSettingsSectionClick = (sectionId: string) => {
    handleNavigation(`/settings#${sectionId}`);
  };

  const handleNavigation = (url: string) => {
    setCurrentRightPaneUrl(url);
  };

  return (
    <Sidebar className="border-r border-border/50">
      <SidebarHeader className="h-16 flex items-center px-6 pt-[15px]">
        <Logo />
      </SidebarHeader>
      <SidebarContent>
        <div className="px-3 mb-4">
          <Button
            variant="outline"
            className="w-full justify-between"
            onClick={openModal}
          >
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4" />
              <span>{currentWebsite?.name || "Select Website"}</span>
            </div>
            <ChevronRight className="h-4 w-4 ml-2 text-muted-foreground" />
          </Button>
        </div>
        <SidebarMenu className="px-3">
          <SidebarMenuItem>
            <SidebarMenuButton
              className="gap-2"
              isActive={currentRightPaneUrl === "/" || currentRightPaneUrl === "/default"}
              onClick={() => handleNavigation("/default")}
            >
              <Home className="h-4 w-4" />
              <span>Dashboard</span>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton
              className="gap-2"
              isActive={currentRightPaneUrl?.startsWith("/planned-content") || false}
              onClick={() => handleNavigation("/planned-content")}
            >
              <Calendar className="h-4 w-4" />
              <span>Planned Content</span>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton
              className="gap-2"
              isActive={currentRightPaneUrl?.startsWith("/sitemap") || false}
              onClick={() => handleNavigation("/sitemap")}
            >
              <Map className="h-4 w-4" />
              <span>Website Content</span>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton
              className="gap-2"
              isActive={currentRightPaneUrl?.startsWith("/media") || false}
              onClick={() => handleNavigation("/media")}
            >
              <Image className="h-4 w-4" />
              <span>Media Library</span>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/* Website Settings restored */}
          <SidebarMenuItem>
            <SidebarMenuButton
              className="gap-2"
              isActive={currentRightPaneUrl?.startsWith("/settings") || false}
              onClick={handleSettingsClick}
            >
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                <span>Website Settings</span>
              </div>
            </SidebarMenuButton>
            {isSettingsExpanded && (
              <SidebarMenuSub>
                {settingsItems.map((item) => (
                  <SidebarMenuSubItem key={item.id}>
                    <SidebarMenuSubButton
                      isActive={currentRightPaneUrl === `/settings#${item.id}`}
                      onClick={() => handleSettingsSectionClick(item.id)}
                      className="flex items-center gap-2"
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            )}
          </SidebarMenuItem>

          {/* Finalise settings kept only in footer */}
        </SidebarMenu>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              className="gap-2"
              isActive={currentRightPaneUrl?.startsWith("/free-credits") || false}
              onClick={() => handleNavigation("/free-credits")}
            >
              <Gift className="h-4 w-4" />
              <span>Get free credits</span>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton
              className="gap-2 text-green-700 dark:text-green-500 border border-green-200 dark:border-green-800 bg-green-50 hover:bg-green-100"
              isActive={false}
              onClick={() => {
                // Guard: if WordPress is connected, do nothing
                try {
                  const wp = (window as any).wpSettingsConnected as boolean;
                  if (wp) {
                    // Open preferences directly (step 2)
                    const params = new URLSearchParams(window.location.search);
                    params.set('show_second_stage', '1');
                    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
                    window.dispatchEvent(new CustomEvent('open-second-stage-onboarding', { detail: { initialStep: 2 } } as any));
                    return;
                  }
                } catch {}
                // Open the second-stage onboarding modal by setting a URL flag
                const params = new URLSearchParams(window.location.search);
                params.set('show_second_stage', '1');
                window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
                // Dispatch a custom event so listeners can open the modal at step 1
                window.dispatchEvent(new CustomEvent('open-second-stage-onboarding', { detail: { initialStep: 1 } } as any));
              }}
            >
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                <span>Finish setup</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton
              className="gap-2"
              isActive={currentRightPaneUrl?.startsWith("/team-management") || false}
              onClick={() => handleNavigation("/team-management")}
            >
              <Users className="h-4 w-4" />
              <span>Team Management</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

export default AppSidebar;
